import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Building2, Loader2, FileText, Shield, Upload, ExternalLink, Globe, Linkedin, MapPin } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { clientService } from '@core/supabase/services/clientService';
import { clientComplianceService } from '@core/supabase/services/clientComplianceService';
import { SplitFlowShell } from '@shared/components/Layout/SplitFlowShell';
import { FlowCoverImage } from '@shared/components/flow/FlowCoverImage';
import { clientFlowImage } from '@shared/constants/flowIllustrationPaths';
import {
  CLIENT_ENGAGEMENT_AGREEMENT_TEXT,
  CLIENT_ENGAGEMENT_CONSENT_VERSION,
  CLIENT_ONBOARDING_EXTERNAL_LINKS,
  CLIENT_ONBOARDING_WIZARD_STEP_LABELS,
  REQUIRED_CLIENT_COMPLIANCE_DOC_TYPES,
} from '@core/constants/clientOnboarding';
import { CLIENT_INDUSTRY_OPTIONS } from './ClientForm';
import { ClientOnboardingWizardStepper } from './ClientOnboardingWizardStepper';
import {
  clearClientNewWizardDraft,
  loadClientNewWizardDraft,
  saveClientNewWizardDraft,
} from './clientNewWizardDraft';

const WIZARD_STEPS = CLIENT_ONBOARDING_WIZARD_STEP_LABELS.length;

function fmtDate(iso: string) {
  if (!iso) return '—';
  const d = new Date(iso + 'T12:00:00');
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function isValidOptionalEmail(s: string): boolean {
  if (!s.trim()) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

const emptyForm = () => ({
  name: '',
  domain: '',
  industry: '',
  company_size: '',
  hq_location: '',
  country: '',
  company_type: '',
  linkedin_url: '',
  about: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  contact_designation: '',
  commercial_agreement_label: '',
  mou_reference: '',
  contract_start_date: '',
  contract_end_date: '',
  billing_frequency: '',
  commercial_terms_summary: '',
  notes: '',
});

const COMPANY_SIZE_OPTIONS = ['1–10', '11–50', '51–200', '201–1,000', '1,001–5,000', '5,000+'];
const COMPANY_TYPE_OPTIONS = ['Startup', 'SME', 'Mid-market', 'Enterprise', 'MNC', 'Government / PSU', 'NGO / Non-profit'];
const BILLING_FREQ_OPTIONS = ['Monthly', 'Quarterly', 'Half-yearly', 'Annually', 'Milestone-based', 'Per-placement'];

/** Inline error message shown directly below the field */
function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return (
    <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-red-600 dark:text-red-400">
      <svg className="h-3.5 w-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      {msg}
    </p>
  );
}

/** Required asterisk */
function Req() {
  return <span className="ml-0.5 text-red-500" aria-hidden>*</span>;
}

export function ClientNewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [maxReached, setMaxReached] = useState(1);
  const [loading, setLoading] = useState(false);
  /** Per-field inline errors */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  /** Step-level (non-field) error, e.g. consent gate */
  const [stepError, setStepError] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [engagementScrolledToEnd, setEngagementScrolledToEnd] = useState(false);
  const [complianceFiles, setComplianceFiles] = useState<Record<string, File | null>>({});
  const [form, setForm] = useState(emptyForm);

  const draftPromptedRef = useRef(false);
  const createInFlightRef = useRef(false);
  const agreementScrollRef = useRef<HTMLDivElement>(null);

  const wizardNavLabels = useMemo(
    () => CLIENT_ONBOARDING_WIZARD_STEP_LABELS.map((l) => `[${l}]`),
    [],
  );

  /** Clears the field's error as soon as the user starts correcting it */
  const set =
    (field: keyof ReturnType<typeof emptyForm>) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      setForm((f) => ({ ...f, [field]: e.target.value }));
      if (fieldErrors[field]) {
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    };

  const bumpReached = useCallback((n: number) => {
    setMaxReached((m) => Math.max(m, n));
  }, []);

  const goToStep = useCallback(
    (n: number) => {
      if (n < 1 || n > WIZARD_STEPS || n > maxReached) return;
      setFieldErrors({});
      setStepError('');
      setStep(n);
    },
    [maxReached],
  );

  useEffect(() => {
    if (!user?.orgId || draftPromptedRef.current) return;
    draftPromptedRef.current = true;
    const d = loadClientNewWizardDraft(user.orgId);
    if (!d) return;
    setForm({ ...emptyForm(), ...d.form });
    setStep(Math.min(WIZARD_STEPS, Math.max(1, d.step)));
    setMaxReached(Math.min(WIZARD_STEPS, Math.max(1, d.maxReached)));
    setConsentAccepted(Boolean(d.consentAccepted));
    setEngagementScrolledToEnd(Boolean(d.engagementScrolledToEnd));
    setComplianceFiles({});
  }, [user?.orgId]);

  useEffect(() => {
    if (!user?.orgId) return;
    const t = window.setTimeout(() => {
      saveClientNewWizardDraft({
        v: 1,
        orgId: user.orgId!,
        step,
        maxReached,
        form,
        consentAccepted,
        engagementScrolledToEnd,
        savedAt: new Date().toISOString(),
      });
    }, 700);
    return () => window.clearTimeout(t);
  }, [user?.orgId, step, maxReached, form, consentAccepted, engagementScrolledToEnd]);

  const onAgreementScroll = useCallback(() => {
    const el = agreementScrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight + 8) { setEngagementScrolledToEnd(true); return; }
    if (scrollTop + clientHeight >= scrollHeight - 32) setEngagementScrolledToEnd(true);
  }, []);

  useEffect(() => {
    if (step !== 3) return;
    const id = window.requestAnimationFrame(() => onAgreementScroll());
    return () => window.cancelAnimationFrame(id);
  }, [step, onAgreementScroll]);

  /* ── step guards: validate inline, only advance if clean ── */
  const goStep2 = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Company name is required.';
    setFieldErrors(errs);
    setStepError('');
    if (Object.keys(errs).length) return;
    bumpReached(2); setStep(2);
  };
  const goStep3 = () => {
    const errs: Record<string, string> = {};
    if (!isValidOptionalEmail(form.contact_email)) errs.contact_email = 'Email address doesn\'t look valid.';
    setFieldErrors(errs);
    setStepError('');
    if (Object.keys(errs).length) return;
    bumpReached(3); setStep(3);
  };
  const goStep4 = () => {
    setFieldErrors({});
    if (!engagementScrolledToEnd) { setStepError('Scroll through the full agreement text first.'); return; }
    if (!consentAccepted) { setStepError('Check the consent box to continue.'); return; }
    setStepError('');
    bumpReached(4); setStep(4);
  };
  const goStep5 = () => {
    const errs: Record<string, string> = {};
    const a = form.contract_start_date, b = form.contract_end_date;
    if (a && b && b < a) errs.contract_end_date = 'End date must be on or after the start date.';
    setFieldErrors(errs);
    setStepError('');
    if (Object.keys(errs).length) return;
    bumpReached(5); setStep(5);
  };
  const goStep6 = () => { setFieldErrors({}); setStepError(''); bumpReached(6); setStep(6); };

  const handleCreate = async () => {
    if (!user?.orgId) return;
    if (createInFlightRef.current) return;
    createInFlightRef.current = true;
    setFieldErrors({}); setStepError(''); setLoading(true);
    try {
      const created = await clientService.create({
        org_id: user.orgId,
        name: form.name.trim(),
        domain: form.domain.trim() || null,
        contact_name: form.contact_name.trim() || null,
        contact_email: form.contact_email.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        industry: form.industry.trim() || null,
        default_masking_template_id: null,
        notes: form.notes.trim() || null,
        commercial_agreement_label: form.commercial_agreement_label.trim() || null,
        mou_reference: form.mou_reference.trim() || null,
        contract_start_date: form.contract_start_date || null,
        contract_end_date: form.contract_end_date || null,
        commercial_terms_summary: form.commercial_terms_summary.trim() || null,
        client_onboarding_status: 'onboarding',
        client_consent_accepted_at: new Date().toISOString(),
        client_consent_version: CLIENT_ENGAGEMENT_CONSENT_VERSION,
      });
      for (const { slug } of REQUIRED_CLIENT_COMPLIANCE_DOC_TYPES) {
        const file = complianceFiles[slug];
        if (file) await clientComplianceService.uploadDocument(user.orgId, created.id, slug, file, user.name ?? null);
      }
      const next = await clientComplianceService.syncClientReadiness(created.id, null);
      clearClientNewWizardDraft();
      navigate(`/clients/${next.id}`);
    } catch (err: unknown) {
      setStepError(err instanceof Error ? err.message : 'Could not create client');
    } finally {
      setLoading(false);
      createInFlightRef.current = false;
    }
  };

  const resetWizard = () => {
    setForm(emptyForm()); setComplianceFiles({}); setConsentAccepted(false);
    setEngagementScrolledToEnd(false); setFieldErrors({}); setStepError('');
    setStep(1); setMaxReached(1);
    clearClientNewWizardDraft();
  };

  if (!user?.orgId) return null;

  /* ── Step metadata ── */
  const STEP_META: Record<number, { icon: React.ReactNode; color: string; bg: string; badge: string }> = {
    1: { icon: <Building2 className="h-5 w-5" />, color: '#8B80F9', bg: 'rgba(139,128,249,0.1)', badge: 'Step 1 of 6' },
    2: { icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>, color: '#6366f1', bg: 'rgba(99,102,241,0.1)', badge: 'Step 2 of 6' },
    3: { icon: <Shield className="h-5 w-5" />, color: '#0ea5e9', bg: 'rgba(14,165,233,0.1)', badge: 'Step 3 of 6' },
    4: { icon: <FileText className="h-5 w-5" />, color: '#10b981', bg: 'rgba(16,185,129,0.1)', badge: 'Step 4 of 6' },
    5: { icon: <Upload className="h-5 w-5" />, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', badge: 'Step 5 of 6' },
    6: { icon: <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>, color: '#8B80F9', bg: 'rgba(139,128,249,0.1)', badge: 'Final step' },
  };
  const STEP_TITLES: Record<number, string> = {
    1: 'Company profile', 2: 'Primary contact', 3: 'Engagement & consent',
    4: 'Commercial terms', 5: 'Compliance documents', 6: 'Review & create',
  };
  const STEP_SUBTITLES: Record<number, string> = {
    1: 'Hiring company basics — same fields as the full client record.',
    2: 'Operational contacts for this employer account.',
    3: 'Read the agreement, then consent. Executed MSA/SOW lives in your contracts process.',
    4: 'MSA/MOU references and contract dates for audits and renewals.',
    5: 'Upload what you have — compliance reviews on the client page.',
    6: 'Confirm details, then create. Recruiting stays gated until required docs are approved.',
  };
  const STEP_NEXT_LABELS: Record<number, string> = {
    1: 'Continue to Contact →', 2: 'Continue to Engagement →',
    3: 'Continue to Commercial →', 4: 'Continue to Documents →',
    5: 'Review & Finish →', 6: '✓ Create Client',
  };

  const meta = STEP_META[step] ?? STEP_META[1];
  const coverTitle = STEP_TITLES[step] ?? '';
  const coverSubtitle = STEP_SUBTITLES[step] ?? '';
  const illustration = <FlowCoverImage src={clientFlowImage(step)} alt="" />;

  const stagedCount = REQUIRED_CLIENT_COMPLIANCE_DOC_TYPES.filter(({ slug }) => complianceFiles[slug]).length;

  /* ── Summary groups for step 6 ── */
  const summaryGroups = [
    {
      step: 1, title: 'Company', color: '#8B80F9',
      rows: [
        { label: 'Name', value: form.name.trim(), required: true },
        { label: 'Domain', value: form.domain.trim() },
        { label: 'Industry', value: form.industry },
        { label: 'HQ', value: form.hq_location.trim() },
        { label: 'Size', value: form.company_size ? `${form.company_size} employees` : '' },
        { label: 'Type', value: form.company_type },
      ],
    },
    {
      step: 2, title: 'Contact', color: '#6366f1',
      rows: [
        { label: 'Name', value: form.contact_name.trim() },
        { label: 'Role', value: form.contact_designation.trim() },
        { label: 'Email', value: form.contact_email.trim() },
        { label: 'Phone', value: form.contact_phone.trim() },
      ],
    },
    {
      step: 3, title: 'Engagement', color: '#0ea5e9',
      rows: [
        {
          label: 'Consent', required: true,
          value: consentAccepted ? `Accepted — v${CLIENT_ENGAGEMENT_CONSENT_VERSION}` : '',
          missingMsg: 'Not accepted — go back to Step 3',
        },
      ],
    },
    {
      step: 4, title: 'Commercial', color: '#10b981',
      rows: [
        { label: 'Agreement', value: form.commercial_agreement_label.trim() },
        { label: 'MOU ref', value: form.mou_reference.trim() },
        { label: 'Billing', value: form.billing_frequency },
        { label: 'Contract', value: form.contract_start_date ? `${fmtDate(form.contract_start_date)} → ${fmtDate(form.contract_end_date)}` : '' },
      ],
    },
    {
      step: 5, title: 'Documents', color: '#f59e0b',
      rows: [
        {
          label: 'Staged', value: stagedCount > 0 ? `${stagedCount} of ${REQUIRED_CLIENT_COMPLIANCE_DOC_TYPES.length} types uploaded` : '',
          hint: stagedCount === 0 ? 'Upload from client overview after creation' : undefined,
        },
      ],
    },
  ];

  /* single nav props object drives the single sticky footer */
  type NavProps = { onBack?: () => void; onNext: () => void; nextLabel: string; isLast?: boolean };
  const navProps: NavProps =
    step === 1 ? { onNext: goStep2, nextLabel: STEP_NEXT_LABELS[1] }
    : step === 2 ? { onBack: () => goToStep(1), onNext: goStep3, nextLabel: STEP_NEXT_LABELS[2] }
    : step === 3 ? { onBack: () => goToStep(2), onNext: goStep4, nextLabel: STEP_NEXT_LABELS[3] }
    : step === 4 ? { onBack: () => goToStep(3), onNext: goStep5, nextLabel: STEP_NEXT_LABELS[4] }
    : step === 5 ? { onBack: () => goToStep(4), onNext: goStep6, nextLabel: STEP_NEXT_LABELS[5] }
    : { onBack: () => goToStep(5), onNext: () => void handleCreate(), nextLabel: loading ? 'Creating…' : STEP_NEXT_LABELS[6], isLast: true };

  /* error border class for fields */
  const errCls = (field: string) =>
    fieldErrors[field] ? 'border-red-500/70 ring-1 ring-red-500/30 focus:ring-red-500/50' : '';

  return (
    <SplitFlowShell
      variant="full"
      rightVariant="theme"
      coverVariant="purple"
      contentMaxClass="max-w-xl lg:max-w-2xl"
      coverTitle={coverTitle}
      coverSubtitle={coverSubtitle}
      stepLabel={wizardNavLabels[step - 1] ?? ''}
      stepCurrent={step}
      stepTotal={WIZARD_STEPS}
      illustration={illustration}
      showProgressBar={false}
      showFlowAssetsHint={false}
    >
      {/*
        3-zone layout — header always visible, body scrolls, footer always visible.
        Every step has identical visual weight regardless of form length.
      */}
      <div className="ggj-wizard-form flex flex-col flex-1 min-h-0">

        {/* ══ ZONE 1: sticky header — nav + stepper ══ */}
        <div className="flex-shrink-0 px-6 pt-4 pb-3 border-b vscode-border"
          style={{ background: 'var(--vscode-bg-secondary)' }}>
          <div className="flex items-center justify-between gap-2 mb-3">
            <Link to="/clients"
              className="inline-flex items-center gap-1.5 text-sm font-medium vscode-text-tertiary hover:vscode-text-secondary transition-colors">
              <ArrowLeft className="h-4 w-4" /> Back to clients
            </Link>
          </div>
          <ClientOnboardingWizardStepper
            labels={wizardNavLabels}
            currentStep={step}
            maxReached={maxReached}
            onSelectStep={goToStep}
          />
        </div>

        {/* ══ ZONE 2: scrollable form body ══ */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">

          {/* step context strip — icon + title only, badge removed (stepper already shows step count) */}
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
              style={{ background: meta.bg, color: meta.color, border: `1.5px solid color-mix(in srgb, ${meta.color} 28%, transparent)` }}>
              {meta.icon}
            </div>
            <h2 className="text-[15px] font-extrabold vscode-text-primary leading-tight">{coverTitle}</h2>
          </div>

          {/* ── Step 1: Company ── */}
          {step === 1 && (
            <div className="space-y-4">

              {/* Company name */}
              <div>
                <label className="label" htmlFor="client-name">Company name<Req /></label>
                <input id="client-name" className={`input ${errCls('name')}`} placeholder="e.g. Acme Corp"
                  value={form.name} onChange={set('name')} autoComplete="organization" />
                <FieldError msg={fieldErrors.name} />
                <p className="mt-1 text-[11px] vscode-text-tertiary">The hiring company you're recruiting for — not your own firm.</p>
              </div>

              {/* Website + LinkedIn */}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="label" htmlFor="client-domain">
                    <Globe className="inline w-3 h-3 mr-0.5 opacity-60" />Website domain
                  </label>
                  <input id="client-domain" className="input" placeholder="acme.com"
                    value={form.domain} onChange={set('domain')} autoComplete="url" />
                  <p className="mt-1 text-[11px] vscode-text-tertiary">Used to auto-fetch company logo and metadata.</p>
                </div>
                <div>
                  <label className="label" htmlFor="client-linkedin">
                    <Linkedin className="inline w-3 h-3 mr-0.5 opacity-60" />LinkedIn company page
                  </label>
                  <input id="client-linkedin" className="input" placeholder="linkedin.com/company/acme"
                    value={form.linkedin_url} onChange={set('linkedin_url')} />
                  <p className="mt-1 text-[11px] vscode-text-tertiary">Helps sourcers reference the client's talent brand.</p>
                </div>
              </div>

              {/* HQ / City + Country */}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="label" htmlFor="client-hq">
                    <MapPin className="inline w-3 h-3 mr-0.5 opacity-60" />Headquarters / city
                  </label>
                  <input id="client-hq" className="input" placeholder="e.g. Bengaluru, KA"
                    value={form.hq_location} onChange={set('hq_location')} />
                </div>
                <div>
                  <label className="label" htmlFor="client-country">Country</label>
                  <input id="client-country" className="input" placeholder="India"
                    value={form.country} onChange={set('country')} />
                </div>
              </div>

              {/* Industry */}
              <div>
                <label className="label" htmlFor="client-industry">Industry</label>
                <select id="client-industry" className="input dash-select-native"
                  value={form.industry} onChange={set('industry')}>
                  <option value="">Select industry…</option>
                  {CLIENT_INDUSTRY_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
                <p className="mt-1 text-[11px] vscode-text-tertiary">Drives job template suggestions and benchmark data.</p>
              </div>

              {/* Size + Type */}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="label" htmlFor="client-size">Company size</label>
                  <select id="client-size" className="input dash-select-native"
                    value={form.company_size} onChange={set('company_size')}>
                    <option value="">Select…</option>
                    {COMPANY_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s} employees</option>)}
                  </select>
                </div>
                <div>
                  <label className="label" htmlFor="client-type">Company type</label>
                  <select id="client-type" className="input dash-select-native"
                    value={form.company_type} onChange={set('company_type')}>
                    <option value="">Select…</option>
                    {COMPANY_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              {/* About */}
              <div>
                <label className="label" htmlFor="client-about">About the company <span className="vscode-text-tertiary font-normal">(optional)</span></label>
                <textarea id="client-about"
                  className="input min-h-[72px] resize-y text-sm"
                  placeholder="2–3 sentences about what the company does, its culture, and why candidates would want to work there…"
                  value={form.about}
                  onChange={set('about')}
                />
                <p className="mt-1 text-[11px] vscode-text-tertiary">Shown to candidates on job listings and application pages for this client.</p>
              </div>

            </div>
          )}

          {/* ── Step 2: Contact ── */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="label" htmlFor="contact-name">Contact name</label>
                  <input id="contact-name" className="input" placeholder="Hiring manager or HR"
                    value={form.contact_name} onChange={set('contact_name')} autoComplete="name" />
                </div>
                <div>
                  <label className="label" htmlFor="contact-designation">Designation</label>
                  <input id="contact-designation" className="input" placeholder="e.g. Head of TA"
                    value={form.contact_designation} onChange={set('contact_designation')} />
                </div>
              </div>
              <div>
                <label className="label" htmlFor="contact-email">Contact email</label>
                <input id="contact-email" className={`input ${errCls('contact_email')}`} type="email"
                  placeholder="talent@client.com"
                  value={form.contact_email} onChange={set('contact_email')} autoComplete="email" />
                <FieldError msg={fieldErrors.contact_email} />
              </div>
              <div>
                <label className="label" htmlFor="contact-phone">Phone</label>
                <input id="contact-phone" className="input" type="tel" placeholder="+91 …"
                  value={form.contact_phone} onChange={set('contact_phone')} autoComplete="tel" />
              </div>
            </div>
          )}

          {/* ── Step 3: Engagement ── */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="flex gap-4 text-sm">
                <a href={CLIENT_ONBOARDING_EXTERNAL_LINKS.terms} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-brand-400 hover:text-brand-300">
                  <ExternalLink className="h-3.5 w-3.5" /> Terms
                </a>
                <a href={CLIENT_ONBOARDING_EXTERNAL_LINKS.privacy} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-brand-400 hover:text-brand-300">
                  <ExternalLink className="h-3.5 w-3.5" /> Privacy
                </a>
              </div>
              <div>
                <label className="label">Engagement agreement<Req /></label>
                <div ref={agreementScrollRef} onScroll={onAgreementScroll}
                  className="h-44 overflow-y-auto rounded-xl border vscode-border"
                  style={{ background: 'color-mix(in srgb, var(--vscode-bg-tertiary) 40%, transparent)' }}>
                  <pre className="whitespace-pre-wrap p-4 text-sm leading-relaxed vscode-text-secondary font-sans">
                    {CLIENT_ENGAGEMENT_AGREEMENT_TEXT}
                  </pre>
                </div>
                <p className={`mt-2 text-sm font-medium ${engagementScrolledToEnd ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                  {engagementScrolledToEnd ? '✓ You\'ve read the full agreement.' : '↓ Scroll to the bottom to unlock consent.'}
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer select-none rounded-xl p-4"
                style={{ background: 'color-mix(in srgb, var(--vscode-bg-secondary) 70%, transparent)', border: '1px solid var(--vscode-border-primary)' }}>
                <input type="checkbox" className="mt-0.5 h-5 w-5 rounded accent-[#8B80F9]"
                  checked={consentAccepted} disabled={!engagementScrolledToEnd}
                  onChange={(e) => setConsentAccepted(e.target.checked)} />
                <span className="text-[14px] leading-snug vscode-text-primary font-medium">
                  I confirm I am authorised to onboard this client and I consent on behalf of my organisation to proceed with GoGrabJob B2B.
                </span>
              </label>
            </div>
          )}

          {/* ── Step 4: Commercial ── */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="label" htmlFor="agr-label">Agreement label</label>
                  <input id="agr-label" className="input" placeholder="e.g. MSA-2026"
                    value={form.commercial_agreement_label} onChange={set('commercial_agreement_label')} />
                </div>
                <div>
                  <label className="label" htmlFor="mou-ref">MOU reference</label>
                  <input id="mou-ref" className="input" placeholder="Document ID or title"
                    value={form.mou_reference} onChange={set('mou_reference')} />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="label" htmlFor="c-start">Contract start</label>
                  <input id="c-start" className="input" type="date"
                    value={form.contract_start_date} onChange={set('contract_start_date')} />
                </div>
                <div>
                  <label className="label" htmlFor="c-end">Contract end / review</label>
                  <input id="c-end" className={`input ${errCls('contract_end_date')}`} type="date"
                    value={form.contract_end_date} onChange={set('contract_end_date')} />
                  <FieldError msg={fieldErrors.contract_end_date} />
                </div>
              </div>
              <div>
                <label className="label" htmlFor="billing-freq">Billing frequency</label>
                <select id="billing-freq" className="input dash-select-native"
                  value={form.billing_frequency} onChange={set('billing_frequency')}>
                  <option value="">Select…</option>
                  {BILLING_FREQ_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="label" htmlFor="comm-summary">Terms summary</label>
                <textarea id="comm-summary" className="input resize-none" rows={3}
                  placeholder="Fees, notice period, exclusivity — brief summary"
                  value={form.commercial_terms_summary} onChange={set('commercial_terms_summary')} />
              </div>
            </div>
          )}

          {/* ── Step 5: Documents ── */}
          {step === 5 && (
            <div className="space-y-3">
              <p className="text-[13px] vscode-text-secondary">
                Upload now or skip and upload from the client page. Files are reviewed before recruiting is unblocked.
              </p>
              {REQUIRED_CLIENT_COMPLIANCE_DOC_TYPES.map(({ slug, label }) => (
                <div key={slug} className="rounded-xl p-4 space-y-2"
                  style={{ background: 'color-mix(in srgb, var(--vscode-bg-secondary) 60%, transparent)', border: '1px solid var(--vscode-border-primary)' }}>
                  <p className="label mb-0 text-[14px] font-semibold vscode-text-primary">{label}</p>
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                    className="block w-full text-sm vscode-text-secondary file:mr-3 file:rounded-lg file:border-0 file:bg-[#8B80F9] file:text-white file:px-3 file:py-1.5 file:text-xs file:font-semibold file:cursor-pointer"
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null;
                      setComplianceFiles((prev) => ({ ...prev, [slug]: f }));
                    }} />
                  {complianceFiles[slug] && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">✓ {complianceFiles[slug]!.name}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Step 6: Review ── */}
          {step === 6 && (
            <div className="space-y-3">
              {/* internal notes */}
              <div>
                <label className="label" htmlFor="client-notes">Internal notes</label>
                <textarea id="client-notes" className="input resize-none" rows={2}
                  placeholder="Billing ID, account manager, anything your team should see."
                  value={form.notes} onChange={set('notes')} />
              </div>

              {/* grouped summary cards */}
              {summaryGroups.map((group) => (
                <div key={group.step} className="rounded-xl border vscode-border overflow-hidden">
                  {/* group header */}
                  <div className="flex items-center justify-between px-4 py-2.5 border-b vscode-border"
                    style={{ background: `color-mix(in srgb, ${group.color} 8%, var(--vscode-bg-secondary))` }}>
                    <div className="flex items-center gap-2">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white"
                        style={{ background: group.color }}>
                        {group.step}
                      </span>
                      <span className="text-xs font-bold uppercase tracking-wider" style={{ color: group.color }}>
                        {group.title}
                      </span>
                    </div>
                    <button type="button" onClick={() => goToStep(group.step)}
                      className="text-[11px] font-semibold transition-opacity hover:opacity-100 opacity-70"
                      style={{ color: group.color }}>
                      Edit →
                    </button>
                  </div>

                  {/* rows */}
                  <div className="divide-y vscode-border">
                    {group.rows.map((row) => {
                      const hasValue = Boolean(row.value);
                      const isMissingRequired = row.required && !hasValue;
                      return (
                        <div key={row.label} className="flex items-start gap-3 px-4 py-2.5">
                          <span className="w-[72px] shrink-0 text-[11px] vscode-text-tertiary font-semibold uppercase tracking-wide pt-0.5">
                            {row.label}
                          </span>
                          <span className="flex-1 min-w-0">
                            {isMissingRequired ? (
                              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-600 dark:text-amber-400">
                                <svg className="h-3.5 w-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                {'missingMsg' in row ? row.missingMsg : 'Required — please fill in'}
                              </span>
                            ) : hasValue ? (
                              <span className="flex items-center gap-2 text-sm vscode-text-primary font-medium">
                                <svg className="h-3.5 w-3.5 shrink-0 text-emerald-500" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="truncate">{row.value}</span>
                              </span>
                            ) : (
                              <span className="text-sm vscode-text-tertiary">
                                {'hint' in row && row.hint ? (
                                  <span className="italic">{row.hint}</span>
                                ) : '—'}
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* step-level error (non-field) */}
          {stepError && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-500/35 bg-red-500/8 px-4 py-3">
              <svg className="h-4 w-4 shrink-0 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm font-medium text-red-700 dark:text-red-300">{stepError}</p>
            </div>
          )}
        </div>

        {/* ══ ZONE 3: sticky footer — buttons always visible ══ */}
        <div className="flex-shrink-0 px-6 py-3 border-t vscode-border"
          style={{ background: 'var(--vscode-bg-secondary)' }}>
          <div className="flex items-center gap-3">
            {navProps.onBack ? (
              <button type="button" onClick={navProps.onBack} disabled={loading}
                className="flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-bold transition-all disabled:opacity-50"
                style={{ color: '#8B80F9', background: 'rgba(139,128,249,0.08)', border: '1.5px solid rgba(139,128,249,0.28)' }}>
                <ArrowLeft className="h-4 w-4 shrink-0" />
                Back
              </button>
            ) : (
              <div className="w-[84px] shrink-0" />
            )}
            <div className="flex-1" />
            <button type="button" onClick={navProps.onNext} disabled={loading}
              className="flex items-center justify-center gap-2 rounded-xl px-8 py-3 text-sm font-bold text-white transition-all disabled:opacity-60"
              style={{
                background: navProps.isLast ? 'linear-gradient(135deg, #7B6EE8 0%, #8B80F9 100%)' : '#8B80F9',
                boxShadow: '0 4px 16px rgba(139,128,249,0.38)',
                minWidth: 190,
              }}>
              {loading && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
              <span>{navProps.nextLabel}</span>
            </button>
          </div>
          {navProps.isLast && (
            <p className="mt-2 text-center text-xs vscode-text-tertiary">
              Wrong data?{' '}
              <button type="button" onClick={resetWizard}
                className="underline underline-offset-2 hover:vscode-text-secondary">
                Start over
              </button>
            </p>
          )}
        </div>

      </div>
    </SplitFlowShell>
  );
}
